import React from 'react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import { formatDistanceToNow } from 'date-fns';
import { fmtDateTime } from '@/lib/dateFormat';
import {
  Users, Plus, Play, Trash2, Pencil, CheckCircle, XCircle, AlertCircle,
  Clock, Calendar, CalendarCheck, ChevronDown, Loader2,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

const INTERVAL_OPTIONS = [
  { value: 'hourly',    label: 'Every hour' },
  { value: 'every_6h', label: 'Every 6 hours' },
  { value: 'every_12h',label: 'Every 12 hours' },
  { value: 'daily',    label: 'Daily' },
  { value: 'weekly',   label: 'Weekly' },
];

function intervalLabel(v: string | null) {
  return INTERVAL_OPTIONS.find((o) => o.value === v)?.label ?? 'Daily';
}

interface CustomerAutomation {
  id: string;
  name: string;
  description: string | null;
  storeId: string;
  storeNickname: string | null;
  storeUrl: string | null;
  conditions: any[];
  actions: any[];
  lastRunAt: string | null;
  lastRunStatus: string | null;
  scheduleEnabled: boolean;
  scheduleInterval: string | null;
  nextRunAt: string | null;
  createdAt: string;
}

function useApi() {
  const { token } = useAuthStore();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const get = async (path: string) => {
    const r = await fetch(`${BASE}/api${path}`, { headers: { ...authHeader } });
    if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
    return r.json();
  };
  const del = async (path: string) => {
    const r = await fetch(`${BASE}/api${path}`, { method: 'DELETE', headers: { ...authHeader } });
    if (!r.ok && r.status !== 204) throw new Error(r.statusText);
  };
  const patch = async (path: string, body: unknown) => {
    const r = await fetch(`${BASE}/api${path}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
    return r.json();
  };
  return { get, del, patch };
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">Never run</span>;
  if (status === 'success') return (
    <span className="flex items-center gap-1 text-xs text-emerald-400">
      <CheckCircle className="w-3.5 h-3.5" /> Success
    </span>
  );
  if (status === 'error') return (
    <span className="flex items-center gap-1 text-xs text-rose-400">
      <XCircle className="w-3.5 h-3.5" /> Error
    </span>
  );
  if (status === 'partial') return (
    <span className="flex items-center gap-1 text-xs text-amber-400">
      <AlertCircle className="w-3.5 h-3.5" /> Partial
    </span>
  );
  return <span className="text-xs text-amber-400">{status}</span>;
}

function SchedulePopover({
  auto,
  onSaved,
}: {
  auto: CustomerAutomation;
  onSaved: (updated: Partial<CustomerAutomation>) => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [enabled, setEnabled] = React.useState(auto.scheduleEnabled);
  const [interval, setInterval] = React.useState(auto.scheduleInterval ?? 'daily');
  const [saving, setSaving] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.patch(`/customer-automations/${auto.id}/schedule`, {
        scheduleEnabled: enabled,
        scheduleInterval: interval,
      });
      onSaved({ scheduleEnabled: updated.scheduleEnabled, scheduleInterval: updated.scheduleInterval, nextRunAt: updated.nextRunAt });
      toast({ title: enabled ? 'Schedule enabled' : 'Schedule disabled' });
      setOpen(false);
    } catch (err: any) {
      toast({ title: 'Error saving schedule', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const isScheduled = auto.scheduleEnabled;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={isScheduled ? `Scheduled: ${intervalLabel(auto.scheduleInterval)}` : 'Set schedule'}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
          isScheduled
            ? 'bg-violet-500/15 border-violet-500/30 text-violet-300 hover:bg-violet-500/25'
            : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:bg-muted'
        }`}
      >
        {isScheduled ? <CalendarCheck className="w-3.5 h-3.5" /> : <Calendar className="w-3.5 h-3.5" />}
        {isScheduled ? intervalLabel(auto.scheduleInterval) : 'Schedule'}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-2xl shadow-2xl z-50 p-4 space-y-4">
          <div>
            <p className="text-xs font-bold text-foreground mb-3">Auto-run schedule</p>

            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Enable schedule</span>
              <button
                onClick={() => setEnabled((e) => !e)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${enabled ? 'bg-violet-500' : 'bg-muted'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-4.5' : 'translate-x-1'}`} />
              </button>
            </div>

            {enabled && (
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Run frequency</label>
                <div className="relative">
                  <select
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                    className="w-full appearance-none bg-muted/50 border border-border rounded-xl pl-3 pr-8 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                  >
                    {INTERVAL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                </div>
                {auto.nextRunAt && auto.scheduleEnabled && auto.scheduleInterval === interval && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Next run {formatDistanceToNow(new Date(auto.nextRunAt), { addSuffix: true })}
                  </p>
                )}
              </div>
            )}
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-foreground text-sm font-medium transition-colors disabled:opacity-60"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

export function CustomerAutomationsList() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const api = useApi();

  const [automations, setAutomations] = React.useState<CustomerAutomation[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState<string | null>(null);

  const load = async () => {
    try {
      const data = await api.get('/customer-automations');
      setAutomations(data);
    } catch (err: any) {
      toast({ title: 'Error loading automations', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this automation?')) return;
    setDeleting(id);
    try {
      await api.del(`/customer-automations/${id}`);
      setAutomations((prev) => prev.filter((a) => a.id !== id));
      toast({ title: 'Automation deleted' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const updateSchedule = (id: string, updates: Partial<CustomerAutomation>) => {
    setAutomations((prev) => prev.map((a) => a.id === id ? { ...a, ...updates } : a));
  };

  return (
    <Layout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-display text-gradient mb-2">Customer Automations</h1>
          <p className="text-muted-foreground">Filter your Shopify customers and execute actions like tagging and SMS.</p>
        </div>
        <Button onClick={() => setLocation('/customer-automations/new')} className="gap-2">
          <Plus className="w-4 h-4" /> Create Automation
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4 animate-pulse">
          {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-muted/50 rounded-2xl" />)}
        </div>
      ) : automations.length === 0 ? (
        <div className="glass-panel rounded-3xl p-16 text-center border-dashed border-border">
          <Users className="w-16 h-16 mx-auto mb-6 text-slate-600" />
          <h3 className="text-xl font-display mb-2">No automations yet</h3>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">Build a customer automation to filter your store's customers and apply bulk actions like adding tags or sending SMS messages.</p>
          <Button onClick={() => setLocation('/customer-automations/new')}>Create your first automation</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {automations.map((auto) => (
            <div key={auto.id} className="glass-panel rounded-2xl p-5 border border-border hover:border-primary/30 transition-all">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-foreground">{auto.name}</h3>
                    {auto.scheduleEnabled && (
                      <span className="flex items-center gap-1 text-xs bg-violet-500/15 border border-violet-500/25 text-violet-300 px-2 py-0.5 rounded-lg">
                        <CalendarCheck className="w-3 h-3" /> {intervalLabel(auto.scheduleInterval)}
                      </span>
                    )}
                  </div>
                  {auto.description && <p className="text-sm text-muted-foreground mb-2">{auto.description}</p>}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {auto.storeNickname && (
                      <span className="bg-primary/10 border border-primary/20 text-primary px-2 py-1 rounded-lg font-medium">
                        {auto.storeNickname}
                      </span>
                    )}
                    <span className="bg-muted/50 border border-border px-2 py-1 rounded-lg">
                      {auto.conditions.length} filter{auto.conditions.length !== 1 ? 's' : ''}
                    </span>
                    <span className="bg-muted/50 border border-border px-2 py-1 rounded-lg">
                      {auto.actions.length} action{auto.actions.length !== 1 ? 's' : ''}
                    </span>
                    {auto.lastRunAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last run {fmtDateTime(auto.lastRunAt)}
                      </span>
                    )}
                    {auto.scheduleEnabled && auto.nextRunAt && (
                      <span className="flex items-center gap-1 text-violet-400">
                        <Calendar className="w-3 h-3" />
                        Next {formatDistanceToNow(new Date(auto.nextRunAt), { addSuffix: true })}
                      </span>
                    )}
                    <StatusBadge status={auto.lastRunStatus} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <SchedulePopover auto={auto} onSaved={(u) => updateSchedule(auto.id, u)} />
                  <Button variant="secondary" size="sm" className="gap-2" onClick={() => setLocation(`/customer-automations/${auto.id}`)}>
                    <Play className="w-3.5 h-3.5" /> View & Run
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setLocation(`/customer-automations/${auto.id}/edit`)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleDelete(auto.id)}
                    isLoading={deleting === auto.id}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
